import React, { useMemo, useState } from 'react';

import { useRetrievalExperience } from '../../../hooks/useRetrievalExperience';
import type { RetrievalPlanStep, RetrievalPlanStepStatus } from '../../../types/retrieval';
import { VerificationBadge } from '../agent';

const statusTone: Record<RetrievalPlanStepStatus, string> = {
  pending: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  running: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  succeeded: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  blocked: 'bg-amber-100 text-amber-700 border-amber-200',
  skipped: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const PlanPanel: React.FC = () => {
  const {
    state,
    requestPlanCommand,
    reorderPlanSteps,
    removePlanStep,
    addPlanStep,
    updatePlanGoal,
  } = useRetrievalExperience();
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [newStepDescription, setNewStepDescription] = useState('');

  const completion = useMemo(() => {
    if (!state.plan || state.plan.steps.length === 0) {
      return { completed: 0, total: 0, percent: 0 };
    }
    const completed = state.plan.steps.filter(
      (step) => step.status === 'completed' || step.status === 'succeeded',
    ).length;
    const total = state.plan.steps.length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  }, [state.plan]);

  const handleAddStep = () => {
    const description = newStepDescription.trim();
    if (!description) {
      return;
    }
    const step: RetrievalPlanStep = {
      id: `user-step-${Date.now()}`,
      type: 'tool_invocation',
      description,
      status: 'pending',
    };
    addPlanStep(step);
    setNewStepDescription('');
  };

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <header className="border-b border-neutral-200 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-neutral-900">Execution Plan</h3>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('curious:insights-open', { detail: { tab: 'plan' } }),
                )
              }
              className="rounded border border-neutral-200 px-2.5 py-1 text-[11px] font-medium text-neutral-600 hover:bg-neutral-50"
            >
              Focus
            </button>
            {state.executionPhase === 'planned' ? (
              <button
                type="button"
                onClick={() => {
                  requestPlanCommand('execute');
                }}
                className="rounded bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-blue-700"
              >
                Execute
              </button>
            ) : null}
          </div>
        </div>
        {state.plan ? (
          <div className="mt-2">
            <label className="text-[10px] uppercase tracking-wide text-neutral-500">Goal</label>
            {isEditingInline ? (
              <input
                value={state.plan.goal}
                onChange={(event) => updatePlanGoal(event.target.value)}
                className="mt-1 w-full rounded border border-neutral-300 px-2 py-1 text-sm text-neutral-800 outline-none focus:border-blue-400"
              />
            ) : (
              <p className="mt-1 text-sm text-neutral-800">{state.plan.goal}</p>
            )}
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-200">
              <div
                className="h-full rounded-full bg-blue-500 transition-all"
                style={{ width: `${completion.percent}%` }}
              />
            </div>
            <p className="mt-1 text-[11px] text-neutral-500">
              {completion.completed}/{completion.total} steps complete
            </p>
          </div>
        ) : (
          <p className="mt-2 text-xs text-neutral-500">
            A retrieval plan will appear when you submit a query.
          </p>
        )}
      </header>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
        {state.plan?.steps.map((step, index) => (
          <article key={step.id} className="rounded-lg border border-neutral-200 bg-white p-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-900">{step.description}</p>
                <p className="text-[11px] uppercase tracking-wide text-neutral-500">{step.type}</p>
                {step.resultSummary ? (
                  <p className="mt-1 text-xs text-neutral-600">{step.resultSummary}</p>
                ) : null}
              </div>
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone[step.status]}`}
              >
                {step.status.replace('_', ' ')}
              </span>
            </div>
            {isEditingInline ? (
              <div className="mt-2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => reorderPlanSteps(index, index - 1)}
                  disabled={index === 0}
                  className="rounded border border-neutral-200 px-2 py-1 text-[11px] text-neutral-600 disabled:opacity-40"
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={() => reorderPlanSteps(index, index + 1)}
                  disabled={index === (state.plan?.steps.length || 1) - 1}
                  className="rounded border border-neutral-200 px-2 py-1 text-[11px] text-neutral-600 disabled:opacity-40"
                >
                  Down
                </button>
                <button
                  type="button"
                  onClick={() => removePlanStep(step.id)}
                  className="rounded border border-red-200 px-2 py-1 text-[11px] text-red-700"
                >
                  Remove
                </button>
              </div>
            ) : null}
          </article>
        ))}

        {state.verificationResults.length > 0 ? (
          <div className="space-y-2 pt-1">
            {state.verificationResults.slice(-1).map((item, index) => (
              <VerificationBadge key={`${item.summary}-${index}`} verification={item} />
            ))}
          </div>
        ) : null}
      </div>

      {state.plan ? (
        <footer className="border-t border-neutral-200 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setIsEditingInline((prev) => !prev)}
              className="rounded border border-neutral-200 px-2.5 py-1 text-[11px] font-medium text-neutral-600 hover:bg-neutral-50"
            >
              {isEditingInline ? 'Done' : 'Quick Edit'}
            </button>
            <button
              type="button"
              onClick={() => {
                requestPlanCommand('pause');
              }}
              className="rounded border border-neutral-200 px-2.5 py-1 text-[11px] font-medium text-neutral-600 hover:bg-neutral-50"
            >
              Advanced Edit
            </button>
          </div>
          {isEditingInline ? (
            <div className="mt-2 flex items-center gap-2">
              <input
                value={newStepDescription}
                onChange={(event) => setNewStepDescription(event.target.value)}
                placeholder="Add custom step"
                className="flex-1 rounded border border-neutral-300 px-2 py-1 text-sm text-neutral-800 outline-none focus:border-blue-400"
              />
              <button
                type="button"
                onClick={handleAddStep}
                className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
};

export default PlanPanel;
